import { create } from 'zustand';
import { logger } from '@/features/help/utils/logger';
import api from '@/features/api/axios';

interface PricingState {
  basePrice: number;
  standardRate: number;
  premiumRate: number;
  isLoading: boolean;
  error: string | null;
  fetchPricing: () => Promise<void>;
}

const DEFAULT_PRICING = {
  basePrice: 200,
  standardRate: 1.0,
  premiumRate: 2.0,
};

export const usePricingStore = create<PricingState>((set) => ({
  ...DEFAULT_PRICING,
  isLoading: false,
  error: null,
  fetchPricing: async () => {
    logger.info('Pricing', 'Fetching pricing data', {}, 'pricing-fetch-start');
    set({ isLoading: true });
    try {
      const { data } = await api.get('/api/subscription/pricing');

      logger.info(
        'Pricing',
        'Pricing data received',
        {
          basePrice: data.basePrice,
          standardApplicationsRate: data.standardApplicationsRate,
          premiumApplicationsRate: data.premiumApplicationsRate,
        },
        'pricing-data-received'
      );

      // Only update if we have valid pricing data
      if (data && Object.keys(data).length > 0) {
        set({
          basePrice: data.basePrice ?? DEFAULT_PRICING.basePrice,
          standardRate: data.standardApplicationsRate ?? DEFAULT_PRICING.standardRate,
          premiumRate: data.premiumApplicationsRate ?? DEFAULT_PRICING.premiumRate,
          isLoading: false,
          error: null,
        });
        logger.info('Pricing', 'Pricing data updated in store', {}, 'pricing-store-updated');
      } else {
        logger.warn('Pricing', 'No pricing data received, using defaults', {}, 'pricing-no-data');
        set({
          ...DEFAULT_PRICING,
          isLoading: false,
          error: null,
        });
      }
    } catch (error) {
      logger.error(
        'Pricing',
        'Failed to fetch pricing data',
        {
          error,
        },
        'pricing-fetch-error'
      );
      set({
        ...DEFAULT_PRICING,
        error: null, // Don't show error to user, just use defaults
        isLoading: false,
      });
    }
  },
}));
